import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Palette,
  Sun,
  Moon,
  Monitor,
  Check,
  Sparkles,
  RotateCcw,
  Save,
  X,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const themePresets = {
  light: [
    { name: "Ocean Blue", value: "from-blue-50 via-white to-blue-100", preview: "from-blue-400 to-blue-600" },
    { name: "Fresh Green", value: "from-green-50 via-white to-green-100", preview: "from-green-400 to-green-600" },
    { name: "Soft Purple", value: "from-purple-50 via-white to-purple-100", preview: "from-purple-400 to-purple-600" },
    { name: "Warm Pink", value: "from-pink-50 via-white to-pink-100", preview: "from-pink-400 to-pink-600" },
    { name: "Sunny Orange", value: "from-orange-50 via-white to-orange-100", preview: "from-orange-400 to-orange-600" },
    { name: "Soft Cyan", value: "from-cyan-50 via-white to-cyan-100", preview: "from-cyan-400 to-cyan-600" },
  ],
  dark: [
    { name: "Midnight", value: "from-gray-900 via-gray-800 to-gray-900", preview: "from-gray-600 to-gray-800" },
    { name: "Deep Ocean", value: "from-slate-900 via-slate-800 to-slate-900", preview: "from-blue-600 to-slate-800" },
    { name: "Violet Night", value: "from-violet-950 via-violet-900 to-violet-950", preview: "from-violet-600 to-violet-900" },
    { name: "Rose Dark", value: "from-rose-950 via-rose-900 to-rose-950", preview: "from-rose-600 to-rose-900" },
    { name: "Emerald Night", value: "from-emerald-950 via-emerald-900 to-emerald-950", preview: "from-emerald-600 to-emerald-900" },
  ],
  gradient: [
    { name: "Sunset Glow", value: "from-orange-400 via-pink-500 to-purple-600", preview: "from-orange-400 via-pink-500 to-purple-600" },
    { name: "Ocean Breeze", value: "from-cyan-400 via-blue-500 to-indigo-600", preview: "from-cyan-400 via-blue-500 to-indigo-600" },
    { name: "Forest Mist", value: "from-green-400 via-emerald-500 to-teal-600", preview: "from-green-400 via-emerald-500 to-teal-600" },
    { name: "Berry Blast", value: "from-pink-500 via-rose-600 to-red-600", preview: "from-pink-500 via-rose-600 to-red-600" },
    { name: "Golden Hour", value: "from-yellow-400 via-amber-500 to-orange-600", preview: "from-yellow-400 via-amber-500 to-orange-600" },
    { name: "Northern Lights", value: "from-teal-400 via-cyan-500 to-blue-600", preview: "from-teal-400 via-cyan-500 to-blue-600" },
  ],
};

const ThemeSelector = () => {
  const { theme: currentTheme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("light");
  const [selectedTheme, setSelectedTheme] = useState(currentTheme);

  const categories = [
    { id: "light", label: "Light", icon: Sun },
    { id: "dark", label: "Dark", icon: Moon },
    { id: "gradient", label: "Gradient", icon: Sparkles },
  ];

  const handleThemeSelect = (themeValue) => {
    setSelectedTheme(themeValue);
    setTheme(themeValue);
  };

  const handleReset = () => {
    setSelectedTheme("from-blue-50 via-white to-blue-100");
    setTheme("from-blue-50 via-white to-blue-100");
  };

  const handleSave = () => {
    // Save to localStorage or API
    localStorage.setItem("selectedTheme", selectedTheme);
    setOpen(false);
  };

  return (
    <div className="fixed right-6 top-1/2 transform -translate-y-1/2 z-50 flex flex-col items-end">
      {/* Main Palette Button */}
      <motion.button
        whileHover={{ scale: 1.1, rotate: 90 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setOpen(!open)}
        className="relative group bg-gradient-to-r from-blue-500 to-purple-600 p-4 rounded-full shadow-2xl shadow-blue-500/30"
      >
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
        <Palette size={28} className="relative text-white" />
        
        {/* Notification dot */}
        {selectedTheme !== currentTheme && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white"
          />
        )}
      </motion.button>

      {/* Theme Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: 100, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="mt-4 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Sparkles size={20} className="text-blue-500" />
                  Theme Selector
                </h3>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              {/* Category Tabs */}
              <div className="flex gap-2">
                {categories.map((cat) => (
                  <motion.button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-sm font-medium transition-all ${
                      activeCategory === cat.id
                        ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
                        : "bg-white text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <cat.icon size={14} />
                    {cat.label}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Theme Grid */}
            <div className="p-4 max-h-80 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3">
                <AnimatePresence mode="wait">
                  {themePresets[activeCategory].map((theme, index) => (
                    <motion.div
                      key={theme.value}
                      initial={{ opacity: 0, scale: 0.8, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleThemeSelect(theme.value)}
                      className={`relative cursor-pointer rounded-xl overflow-hidden group ${
                        selectedTheme === theme.value ? "ring-2 ring-blue-500 ring-offset-2" : ""
                      }`}
                    >
                      {/* Preview */}
                      <div
                        className={`h-16 w-full bg-gradient-to-br ${theme.preview} flex items-center justify-center`}
                      >
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <Check size={24} className="text-white drop-shadow-lg" />
                        </div>
                      </div>

                      {/* Selected Checkmark */}
                      {selectedTheme === theme.value && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute top-1 right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center"
                        >
                          <Check size={14} className="text-white" />
                        </motion.div>
                      )}

                      {/* Theme Name */}
                      <p className="text-xs text-center py-1 bg-gray-50 text-gray-600 truncate px-1">
                        {theme.name}
                      </p>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleReset}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-white border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <RotateCcw size={16} />
                Reset
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSave}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <Save size={16} />
                Apply
              </motion.button>
            </div>

            {/* Live Preview */}
            <div
              className={`h-3 bg-gradient-to-r ${selectedTheme}`}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Button - Quick Switch */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => {
          const isDark = currentTheme.includes("gray-900") || currentTheme.includes("gray-800");
          setTheme(isDark ? "from-blue-50 via-white to-blue-100" : "from-gray-900 via-gray-800 to-gray-900");
        }}
        className="mt-3 p-3 bg-white/90 backdrop-blur rounded-full shadow-lg hover:shadow-xl transition-shadow"
      >
        <Monitor size={20} className="text-gray-600" />
      </motion.button>
    </div>
  );
};

export default ThemeSelector;
